import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { baseUrl } from "../../config/config";

const fmtSlot = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString();
};

export default function SelfSchedule() {
  const { token } = useParams();
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [booked, setBooked] = useState(null);

  useEffect(() => {
    document.title = "Schedule your interview - Ample Hire";
    axios
      .get(`${baseUrl}/job-posting/api/public/schedule/${token}`)
      .then((r) => setLink(r.data))
      .catch(() => setLink(null))
      .finally(() => setLoading(false));
  }, [token]);

  const book = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const r = await axios.post(`${baseUrl}/job-posting/api/public/schedule/${token}/book`, {
        slotStart: selected.start,
        slotEnd: selected.end,
      });
      setBooked(r.data);
    } catch (e) {
      alert(e?.response?.data?.error || "Failed to book slot.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="p-10 text-center text-slate-500">Loading…</div>;
  if (!link) return <div className="p-10 text-center text-slate-500">Scheduling link not found.</div>;

  if (booked || link.status === "BOOKED") {
    const start = booked?.startTime || link.selectedSlotStart;
    const end = booked?.endTime || link.selectedSlotEnd;
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md bg-white border border-slate-200 rounded-xl p-10 text-center">
          <div className="text-5xl mb-3">📅</div>
          <h2 className="text-2xl font-semibold text-slate-900">Interview confirmed</h2>
          <div className="mt-3 text-sm text-slate-700">
            <div className="font-medium">{fmtSlot(start)}</div>
            <div className="text-xs text-slate-500">to {fmtSlot(end)}</div>
          </div>
          {(booked?.meetingLink || link.meetingLink) && (
            <a
              href={booked?.meetingLink || link.meetingLink}
              target="_blank" rel="noreferrer"
              className="inline-block mt-4 px-4 py-2 rounded bg-vf-primary text-white text-sm"
            >
              Join meeting
            </a>
          )}
        </div>
      </div>
    );
  }

  const slots = Array.isArray(link.availableSlots)
    ? link.availableSlots
    : (() => { try { return JSON.parse(link.availableSlots || "[]"); } catch { return []; } })();

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-6">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-semibold text-slate-900">Schedule your interview</h1>
          <p className="text-sm text-slate-500 mt-1">{link.durationMinutes} minutes · {link.location || "Online"}</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6">
          {link.instructions && (
            <div className="mb-4 p-3 rounded bg-slate-50 text-sm text-slate-700">{link.instructions}</div>
          )}
          <div className="text-sm font-medium text-slate-900 mb-3">Pick a time:</div>
          {slots.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">No slots available. Please contact the recruiter.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {slots.map((s, i) => (
                <button
                  key={i}
                  onClick={() => setSelected(s)}
                  className={`px-3 py-2 rounded border text-sm ${selected === s ? "bg-vf-primary text-white border-vf-primary" : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"}`}
                >
                  {fmtSlot(s.start)}
                </button>
              ))}
            </div>
          )}
        </div>

        {selected && (
          <div className="mt-4 flex justify-center">
            <button
              onClick={book}
              disabled={busy}
              className="px-5 py-2.5 rounded bg-vf-primary text-white font-medium disabled:opacity-50"
            >
              {busy ? "Booking…" : `Confirm ${fmtSlot(selected.start)}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
